import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@fantaz/shared/app-config';
import { ConfigApiService } from '@fantaz/shared/data-access/api';
import { Address, FantazUIError } from '@fantaz/shared/data-access/models';
import { formatDate } from '@fantaz/shared/utils/date';
import {
  DateValidators,
  FieldValidators,
  SSNValidator,
} from '@fantaz/shared/utils/forms';
import { NgFormsManager } from '@ngneat/forms-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

interface EditInfoProps {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: Partial<Address>;
  ssn?: string;
}

type EditInfoDialogProps = {
  onSubmit: (data: EditInfoProps) => void;
  closeSignal$: Observable<boolean>;
  submitting$: Observable<boolean>;
  error$: Observable<FantazUIError>;
  initialData?: Partial<EditInfoProps>;
};

// Controls in a form group that would emit a Person as it's value
type EditInfoFormControls = { [key in keyof EditInfoProps]: AbstractControl };
type EditInfoFormFormGroup = FormGroup & {
  value: EditInfoProps;
  controls: EditInfoFormControls;
};

@UntilDestroy()
@Component({
  selector: 'fz-edit-info-dialog',
  templateUrl: './edit-info-dialog.component.html',
  styleUrls: ['./edit-info-dialog.component.scss'],
})
export class EditInfoDialogComponent implements OnInit, OnDestroy {
  private builder = inject(FormBuilder);
  private formsManager = inject(NgFormsManager);
  editInfoForm: EditInfoFormFormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private configApiService: ConfigApiService,
    public dialogRef: MatDialogRef<EditInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditInfoDialogProps,
  ) {}

  ngOnInit() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: 'edit-info' },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
    // create for
    this.editInfoForm = this.builder.group({
      firstName: [
        null,
        [
          FieldValidators.requiredValidator('firstName'),
          Validators.required,
          Validators.minLength(2),
        ],
      ],
      lastName: [
        null,
        [
          FieldValidators.requiredValidator('lastName'),
          Validators.required,
          Validators.minLength(2),
        ],
      ],
      dateOfBirth: [null, [Validators.required, DateValidators.ageValidator]],
      address: [null, [Validators.required]],
      ssn: [
        null,
        [
          this.FULL_SSN_ALLOWED
            ? SSNValidator.nineDigits
            : SSNValidator.fourDigits,
        ],
      ],
    }) as EditInfoFormFormGroup;

    //
    this.formsManager.upsert('infoForm', this.editInfoForm);

    // set initial data
    if (this.data.initialData) {
      const dateOfBirth = this.data.initialData.dateOfBirth
        ? formatDate(this.data.initialData.dateOfBirth)
        : null;
      this.editInfoForm.patchValue({
        ...this.data.initialData,
        address: new Address({
          ...this.data.initialData.address,
        }),
        dateOfBirth,
      });
    }

    // subscribe to close signal
    if (this.data.closeSignal$) {
      this.data.closeSignal$.pipe(untilDestroyed(this)).subscribe((value) => {
        if (value) {
          this.dialogRef.close({
            source: 'signal',
          });
        }
      });
    }
  }

  ngOnDestroy() {
    this.formsManager.unsubscribe('infoForm');
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: null },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
  }

  get TSEVO_DISABLED() {
    return this.configApiService.isTSEVODisabled();
  }

  get FULL_SSN_ALLOWED() {
    return environment.tsevoConfig?.FULL_SSN_ALLOWED;
  }

  handleFormSubmit(event: any) {
    event.preventDefault();

    this.editInfoForm.markAllAsTouched();

    if (this.editInfoForm.valid && this.data.onSubmit) {
      const dateString = this.editInfoForm.value.dateOfBirth.replace(
        new RegExp('/', 'g'),
        '',
      ); // This is a patch for local tests
      const month = dateString.substring(0, 2);
      const day = dateString.substring(2, 4);
      const year = dateString.substring(4);

      this.data.onSubmit({
        ...this.editInfoForm.value,
        dateOfBirth: `${year}-${month}-${day}`,
        entity: false,
      });
    }
  }

  handleClose() {
    this.dialogRef.close({
      source: 'button',
    });
  }
}
